// ============================================================
// VCS.DatingProfile — Dating-Profil anlegen und bearbeiten
// ============================================================
// Pendant zu dating_profile_screen.dart. Bild und Text sind Pflicht — ohne
// beides gibt es kein Dating-Profil.
//
// Das Formular wird per Template-String gerendert, der Zustand liegt in
// this._form. Jede Eingabe schreibt sofort dorthin: Ein Wechsel des Modus
// blendet Abschnitte ein und aus, und ohne eigenen Zustand waere dabei alles
// weg, was schon getippt war.
// ============================================================
window.VCS = window.VCS || {};

VCS.DatingProfile = class {

    // ── Konstanten ───────────────────────────────────────────
    // 1080 px wie in der App (image_picker maxWidth/maxHeight), Qualitaet 85.

    static PHOTO_MAX_PX  = 1080;
    static PHOTO_QUALITY = 0.85;

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._view = {
            body: $('#datingProfileBody')
        };

        this._profile = null;
        // Serverseitig liegende Fotos: [{ hash, url }] — url ist eine Blob-URL,
        // weil /dating/photo/:hash den Auth-Header braucht.
        this._photos  = [];
        // Nur lokal vorgemerkt: [{ file, url }]. Siehe _addPhoto().
        this._pending = [];
        this._form    = this._emptyForm();
        this._busy    = false;
        // Signatur des zuletzt gespeicherten (bzw. geladenen) Zustands. Dagegen
        // prueft isDirty(), ob ungespeicherte Aenderungen vorliegen. Beim Anlegen
        // schon gesetzt, damit isDirty() nie gegen undefined vergleicht.
        this._clean   = this._formSignature();
    }

    _emptyForm() {
        return {
            displayName: '',
            bio        : '',
            birthYear  : '',
            // Monat/Tag nur fuers Sternzeichen (2026-07-18). Das Alter kommt aus birthYear.
            birthMonth : '',
            birthDay   : '',
            // Optionale Profilangaben. '' bzw. leeres Set = keine Angabe.
            heightCm   : '',
            smoking    : '',
            drinking   : '',
            interests  : new Set(),
            gender     : VCS.DatingCore.GENDERS[0],
            seeking     : VCS.DatingCore.SEEKING[VCS.DatingCore.SEEKING.length - 1],
            // Voreinstellung „egal" — die volle Spanne. Wer sie einengen will,
            // tut das bewusst.
            seekAgeMin : VCS.DatingCore.AGE_MIN,
            seekAgeMax : VCS.DatingCore.AGE_MAX,
            // Anschlag = „egal", aus demselben Grund wie die Altersspanne — und
            // weil ein leerer Stapel schlimmer ist als ein weiter.
            seekRadiusKm: VCS.DatingCore.RADIUS_ANY,
            // Voreingestellt ist immer das Ungefaehre — genau muss man wollen,
            // nicht erben.
            cellPrecision: VCS.DatingCore.PRECISION_ROUGH,
            mode       : 'local',
            country    : '',
            langs      : new Set(),
            // Voreinstellung wie in der App: „einfach quatschen" ist die
            // harmloseste Annahme, wenn noch nichts gesagt wurde.
            intents    : new Set(['talk'])
        };
    }

    init() {
        this._bindEvents();
    }

    // ── Lifecycle ────────────────────────────────────────────

    async onShow() {
        this._renderLoading();
        await this._load();
        this._render();
    }

    // Wird von dating-ui.js beim Verlassen der View gerufen. Blob-URLs sind
    // unser Speicher — die gibt sonst niemand frei.
    onHide() {
        this._releaseUrls();
    }

    // Sprachwechsel: neu zeichnen, aber nur wenn die View gerade offen ist.
    // Der Formular-Zustand steht in this._form und ueberlebt das Rendern.
    onLangChange() {
        if (VCS.dating?.currentView?.() === 'profile' && VCS.dating?.isOpen?.()) {
            this._render();
        }
    }

    // ── Ungespeicherte Aenderungen ───────────────────────────
    //
    // Explizites Speichern ist die bewusste Entscheidung des Projekts. Der Preis
    // dafuer: Wer die View verlaesst, ohne zu speichern, verliert seine Eingaben
    // lautlos. Deshalb merkt sich isDirty() jede Abweichung vom zuletzt geladenen
    // bzw. gespeicherten Zustand, und dating-ui/navigation fragen vor jedem
    // Verlassen nach (confirmLeave()).

    // Stabile Signatur des Formularzustands. Fotos zaehlen nur als "vorgemerkt"
    // (this._pending): bei bestehendem Profil werden Hochladen, Loeschen und
    // Umsortieren sofort gespeichert, sind also nie ungespeichert.
    _formSignature() {
        const f = this._form;
        return JSON.stringify({
            displayName  : f.displayName || '',
            bio          : f.bio || '',
            birthYear    : f.birthYear || '',
            birthMonth   : f.birthMonth || '',
            birthDay     : f.birthDay || '',
            heightCm     : f.heightCm || '',
            smoking      : f.smoking || '',
            drinking     : f.drinking || '',
            interests    : [...f.interests].sort(),
            gender       : f.gender,
            seeking      : f.seeking,
            seekAgeMin   : f.seekAgeMin,
            seekAgeMax   : f.seekAgeMax,
            seekRadiusKm : f.seekRadiusKm,
            cellPrecision: f.cellPrecision,
            mode         : f.mode,
            country      : f.country || '',
            langs        : [...f.langs].sort(),
            intents      : [...f.intents].sort(),
            pending      : this._pending.length
        });
    }

    isDirty() {
        return this._formSignature() !== this._clean;
    }

    // true = Verlassen erlaubt (nichts offen oder Nutzer will verwerfen),
    // false = bleiben. Wird von dating-ui/navigation vor jedem Verlassen gerufen.
    async confirmLeave() {
        if (!this.isDirty()) {
            return true;
        }
        return (await VCS.ui?.showConfirm?.(t('datingUnsavedBody'), '', t('datingUnsavedLeave'))) === true;
    }

    // Der Anzeigename fuer Dating ist immer der allgemeine Profilname (localStorage
    // 'chat-name', gespiegelt in VCS.state.chatMyName). State bevorzugt, localStorage
    // als Fallback, falls der State noch nicht befuellt war.
    _profileName() {
        const fromState = (VCS.state?.chatMyName || '').trim();
        if (fromState) {
            return fromState;
        }
        try {
            return (localStorage.getItem('chat-name') || '').trim();
        } catch (_) {
            return '';
        }
    }

    // ── Laden ────────────────────────────────────────────────

    async _load() {
        this._releaseUrls();

        const profile = await VCS.datingCore.getProfile();
        this._profile = profile;
        this._form    = this._emptyForm();

        if (profile) {
            this._form.displayName = profile.displayName || '';
            this._form.bio         = profile.bio || '';
            this._form.birthYear   = profile.birthYear > 0 ? String(profile.birthYear) : '';
            this._form.birthMonth  = profile.birthMonth > 0 ? String(profile.birthMonth) : '';
            this._form.birthDay    = profile.birthDay > 0 ? String(profile.birthDay) : '';
            this._form.heightCm    = profile.heightCm > 0 ? String(profile.heightCm) : '';
            this._form.smoking     = VCS.DatingCore.HABITS.includes(profile.smoking) ? profile.smoking : '';
            this._form.drinking    = VCS.DatingCore.HABITS.includes(profile.drinking) ? profile.drinking : '';
            this._form.interests   = new Set(Array.isArray(profile.interests) ? profile.interests : []);
            this._form.gender      = profile.gender || this._form.gender;
            this._form.seeking     = profile.seeking || this._form.seeking;
            this._form.seekAgeMin  = profile.seekAgeMin || VCS.DatingCore.AGE_MIN;
            this._form.seekAgeMax  = profile.seekAgeMax || VCS.DatingCore.AGE_MAX;
            this._form.seekRadiusKm = profile.seekRadiusKm || VCS.DatingCore.RADIUS_ANY;
            this._form.cellPrecision = VCS.DatingCore.PRECISIONS.includes(profile.cellPrecision)
                ? profile.cellPrecision : VCS.DatingCore.PRECISION_ROUGH;
            this._form.mode        = profile.mode || 'local';
            this._form.country     = profile.country || '';
            this._form.langs       = new Set(profile.langs || []);
            // Leer heisst „noch nichts gesagt" (Fern-Modus, oder Profil aus der
            // Zeit vor der Mehrfachauswahl) — dann bleibt die Voreinstellung.
            if (profile.intents && profile.intents.length) {
                this._form.intents = new Set(profile.intents);
            }
        }

        // Der Anzeigename kommt IMMER aus dem allgemeinen Profil, nie aus dem Dating-
        // Datensatz -- hier ist er nicht editierbar (siehe _render und _save). Ein evtl.
        // abweichender alter Dating-Name wird beim naechsten Speichern nachgezogen.
        this._form.displayName = this._profileName();

        // Ohne Profil gibt es keinen Handle und damit auch keine Fotos.
        const hashes = profile ? await VCS.datingCore.getPhotos() : [];
        const loaded = [];
        for (const hash of hashes) {
            const url = await VCS.datingCore.loadPhotoUrl(hash);
            loaded.push({ hash: hash, url: url });
        }
        this._photos = loaded;
        // Frisch geladen = sauberer Ausgangszustand. Ab hier zaehlt jede Aenderung
        // am Formular als ungespeichert (isDirty()).
        this._clean = this._formSignature();
        console.log('[dating] Profil-View geladen: profil=' + (profile ? 'ja' : 'nein')
            + ' fotos=' + loaded.length);
    }

    _releaseUrls() {
        for (const p of this._photos) {
            VCS.datingCore?.releasePhotoUrl?.(p.url);
        }
        for (const p of this._pending) {
            VCS.datingCore?.releasePhotoUrl?.(p.url);
        }
        this._photos  = [];
        this._pending = [];
    }

    _photoCount() {
        return this._photos.length + this._pending.length;
    }

    // ── Rendern ──────────────────────────────────────────────

    _renderLoading() {
        this._view.body.html('<div class="dating-loading"><div class="dating-spinner"></div></div>');
    }

    _render() {
        const f = this._form;
        const showIntents  = f.mode !== 'remote';   // Absicht nur beim Treffen vor Ort
        const showCountry  = f.mode !== 'local';    // Land nur, wenn es gebraucht wird
        const showRadius   = f.mode === 'local';    // Umkreis nur, wo er zaehlt

        const genderLabel = (v) => v === 'f' ? t('datingGenderF') : v === 'm' ? t('datingGenderM') : t('datingGenderOther');
        const seekLabel   = (v) => v === 'f' ? t('datingSeekingF') : v === 'm' ? t('datingSeekingM') : t('datingSeekingAny');

        const html = [
            '<p class="dating-intro">' + escapeHtml(t('datingIntro')) + '</p>',

            this._section(t('datingPhotos')),
            '<div class="dating-photo-row" id="datingPhotoRow">' + this._photoRowHtml() + '</div>',
            '<p class="dating-hint">' + escapeHtml(t('datingPhotoHint')) + '</p>',

            this._section(t('datingName')),
            // Der Name kommt aus dem allgemeinen Profil (Anmeldung/Einstellungen) und ist
            // hier bewusst NICHT editierbar: Ein Dating-Name, der vom echten Namen abweicht,
            // waere eine zweite Identitaet. readonly statt disabled, damit der Wert gut
            // lesbar bleibt (disabled graut ihn oft bis zur Unlesbarkeit aus).
            '<input type="text" class="dating-input dating-input--locked"'
                + ' value="' + escapeHtml(f.displayName) + '"'
                + ' readonly aria-readonly="true" tabindex="-1">',

            this._section(t('datingBirthDate')),
            this._birthDateHtml(),

            this._section(t('datingGender')),
            this._chipsHtml('gender', VCS.DatingCore.GENDERS, f.gender, genderLabel),

            // „Ueber mich"-Angaben (optional, 2026-07-18): Groesse, Rauchen, Alkohol,
            // Interessen. Bewusst hier gebuendelt, VOR „ich suche" — erst wer ich bin,
            // dann wen ich suche.
            this._section(t('datingHeight')),
            '<input type="number" class="dating-input" data-dating-field="heightCm"'
                + ' inputmode="numeric" min="' + VCS.DatingCore.HEIGHT_MIN + '" max="' + VCS.DatingCore.HEIGHT_MAX + '"'
                + ' value="' + escapeHtml(f.heightCm) + '" placeholder="' + escapeHtml(t('datingHeightHint')) + '">',

            this._section(t('datingSmoking')),
            this._habitChipsHtml('smoking', f.smoking),

            this._section(t('datingDrinking')),
            this._habitChipsHtml('drinking', f.drinking),

            this._section(t('datingInterests')),
            this._interestsHtml(),

            this._section(t('datingSeeking')),
            this._chipsHtml('seeking', VCS.DatingCore.SEEKING, f.seeking, seekLabel),

            this._section(t('datingSeekAge')),
            this._seekAgeHtml(),

            this._section(t('datingMode')),
            this._modeTileHtml('local',  t('datingModeLocal'),  t('datingModeLocalHint')),
            this._modeTileHtml('remote', t('datingModeRemote'), t('datingModeRemoteHint')),
            this._modeTileHtml('both',   t('datingModeBoth'),   t('datingModeBothHint')),

            // Der Umkreis gehoert zu „nur in meiner Naehe" und zu sonst nichts:
            // Bei „Beides" ist der Ort egal, im Fern-Modus zaehlt das Land. Ein
            // Regler, der in zwei von drei Faellen wirkungslos ist, waere eine
            // Frage, deren Antwort man wegwirft.
            '<div id="datingRadiusSection" class="' + (showRadius ? '' : 'hidden') + '">',
            this._section(t('datingSeekRadius')),
            this._seekRadiusHtml(),
            '</div>',

            // Genauigkeit gibt es nur, wo es einen Standort gibt. Im Fern-Modus
            // wird nie geortet — die Frage waere dort ohne Gegenstand.
            '<div id="datingPrecisionSection" class="' + (showIntents ? '' : 'hidden') + '">',
            this._section(t('datingPrecision')),
            this._precisionTileHtml(VCS.DatingCore.PRECISION_ROUGH,
                t('datingPrecisionRough'), t('datingPrecisionRoughHint')),
            this._precisionTileHtml(VCS.DatingCore.PRECISION_EXACT,
                t('datingPrecisionExact'), t('datingPrecisionExactHint')),
            '</div>',

            '<div id="datingIntentSection" class="' + (showIntents ? '' : 'hidden') + '">',
            this._section(t('datingIntent')),
            '<p class="dating-hint dating-hint--above">' + escapeHtml(t('datingIntentHint')) + '</p>',
            this._intentTileHtml('talk',         t('datingIntentTalk'),         t('datingIntentTalkHint')),
            this._intentTileHtml('relationship', t('datingIntentRelationship'), t('datingIntentRelationshipHint')),
            this._intentTileHtml('sex',          t('datingIntentSex'),          t('datingIntentSexHint')),
            '</div>',

            '<div id="datingCountrySection" class="' + (showCountry ? '' : 'hidden') + '">',
            this._section(t('datingCountry')),
            this._countryHtml(),
            '</div>',

            this._section(t('datingLangs')),
            this._langsHtml(),

            this._section(t('datingBio')),
            '<textarea class="dating-input dating-textarea" data-dating-field="bio" rows="4" maxlength="500"'
                + ' placeholder="' + escapeHtml(t('datingBioHint')) + '">' + escapeHtml(f.bio) + '</textarea>',

            '<button type="button" class="dating-save" data-dating-save>' + escapeHtml(t('datingSave')) + '</button>',

            this._profile ? this._activeSectionHtml() : '',

            '<div class="dating-busy' + (this._busy ? '' : ' hidden') + '" id="datingBusy"><div class="dating-spinner"></div></div>'
        ].join('');

        this._view.body.html(html);
        // Fuellbalken und Griff-Reihenfolge haengen an den Werten, nicht am
        // Markup — sie muessen auch ohne die erste Bewegung stimmen.
        this._updateSeekAge();
        this._updateSeekRadius();
    }

    _section(title) {
        return '<div class="dating-section">' + escapeHtml(title) + '</div>';
    }

    // Gesuchte Altersspanne. HTML kennt keinen Schieber mit zwei Griffen, also
    // liegen hier zwei <input type="range"> uebereinander: Die Spur ist eigenes
    // Markup, von den Eingaben ist nur der Griff sichtbar und anfassbar.
    _seekAgeHtml() {
        const lo = VCS.DatingCore.AGE_MIN;
        const hi = VCS.DatingCore.AGE_MAX;
        const f  = this._form;
        return '<div class="dating-range-head">'
            + '<span class="dating-hint dating-range-note">' + escapeHtml(t('datingSeekAgeHint')) + '</span>'
            + '<span class="dating-range-value" id="datingSeekAgeValue">'
            + escapeHtml(this._seekAgeLabel()) + '</span>'
            + '</div>'
            + '<div class="dating-range">'
            + '<div class="dating-range-track"><div class="dating-range-fill" id="datingSeekAgeFill"></div></div>'
            + '<input type="range" class="dating-range-input" data-dating-age="min"'
            + ' min="' + lo + '" max="' + hi + '" value="' + f.seekAgeMin + '"'
            + ' aria-label="' + escapeHtml(t('datingSeekAge')) + '">'
            + '<input type="range" class="dating-range-input" data-dating-age="max"'
            + ' min="' + lo + '" max="' + hi + '" value="' + f.seekAgeMax + '"'
            + ' aria-label="' + escapeHtml(t('datingSeekAge')) + '">'
            + '</div>';
    }

    // – = Halbgeviertstrich, wie in der App ('18 – 99 Jahre').
    _seekAgeLabel() {
        return this._form.seekAgeMin + ' – ' + this._form.seekAgeMax + ' ' + t('datingSeekAgeYears');
    }

    // Umkreis: nur EIN Griff, deshalb ein schlichtes <input type="range"> statt der
    // zwei uebereinanderliegenden von der Altersspanne. Die Spur ist dieselbe —
    // der Fuellbalken laeuft hier nur von links bis zum Griff.
    _seekRadiusHtml() {
        const f = this._form;
        return '<div class="dating-range-head">'
            + '<span class="dating-hint dating-range-note">' + escapeHtml(t('datingSeekRadiusHint')) + '</span>'
            + '<span class="dating-range-value" id="datingSeekRadiusValue">'
            + escapeHtml(this._seekRadiusLabel()) + '</span>'
            + '</div>'
            + '<div class="dating-range dating-range--single">'
            + '<div class="dating-range-track"><div class="dating-range-fill" id="datingSeekRadiusFill"></div></div>'
            + '<input type="range" class="dating-range-input" data-dating-radius'
            + ' min="' + VCS.DatingCore.RADIUS_MIN + '" max="' + VCS.DatingCore.RADIUS_ANY + '"'
            + ' value="' + f.seekRadiusKm + '"'
            + ' aria-label="' + escapeHtml(t('datingSeekRadius')) + '">'
            + '</div>';
    }

    // Am Anschlag steht „Egal" statt „500 km": Das ist die ehrlichere Aussage, denn
    // dort filtert der Server gar nicht erst.
    _seekRadiusLabel() {
        const f = this._form;
        return f.seekRadiusKm >= VCS.DatingCore.RADIUS_ANY
            ? t('datingSeekRadiusAny')
            : f.seekRadiusKm + ' ' + t('datingSeekRadiusUnit');
    }

    _updateSeekRadius() {
        const lo   = VCS.DatingCore.RADIUS_MIN;
        const hi   = VCS.DatingCore.RADIUS_ANY;
        const f    = this._form;

        $('#datingSeekRadiusValue').text(this._seekRadiusLabel());

        const fill = document.getElementById('datingSeekRadiusFill');
        if (fill) {
            fill.style.left  = '0%';
            fill.style.right = ((hi - f.seekRadiusKm) / (hi - lo) * 100) + '%';
        }
    }

    _updateSeekAge() {
        const lo   = VCS.DatingCore.AGE_MIN;
        const hi   = VCS.DatingCore.AGE_MAX;
        const span = hi - lo;
        const f    = this._form;

        $('#datingSeekAgeValue').text(this._seekAgeLabel());

        const fill = document.getElementById('datingSeekAgeFill');
        if (fill) {
            fill.style.left  = ((f.seekAgeMin - lo) / span * 100) + '%';
            fill.style.right = ((hi - f.seekAgeMax) / span * 100) + '%';
        }

        // Liegen beide Griffe aufeinander, faengt sonst immer derselbe den
        // Zugriff ab und die Spanne liesse sich nie wieder oeffnen. Deshalb
        // kommt der Griff nach oben, der noch Platz vor sich hat.
        $('[data-dating-age="min"]').css('z-index', f.seekAgeMin > (lo + hi) / 2 ? 4 : 2);
    }

    _chipsHtml(field, values, selected, labelFn) {
        const chips = values.map((v) =>
            '<button type="button" class="dating-chip' + (v === selected ? ' is-active' : '') + '"'
                + ' data-dating-choice="' + field + '" data-value="' + escapeHtml(v) + '">'
                + escapeHtml(labelFn(v)) + '</button>'
        ).join('');
        return '<div class="dating-chips">' + chips + '</div>';
    }

    _modeTileHtml(value, title, hint) {
        const on = this._form.mode === value;
        return '<button type="button" class="dating-option' + (on ? ' is-active' : '') + '"'
            + ' data-dating-mode="' + escapeHtml(value) + '">'
            + '<span class="dating-option-mark dating-option-mark--radio"></span>'
            + '<span class="dating-option-text">'
            + '<span class="dating-option-title">' + escapeHtml(title) + '</span>'
            + '<span class="dating-option-hint">' + escapeHtml(hint) + '</span>'
            + '</span></button>';
    }

    // Wie genau man sich zeigt. Radio wie beim Modus: Eins von beidem.
    // Der Hinweistext beschoenigt nichts — wer sich genau zeigt, soll wissen,
    // worauf er sich einlaesst. Das ist hier kein Detail, sondern der Grund,
    // warum die Einwilligung das fruehere Verbot ersetzen darf.
    _precisionTileHtml(value, title, hint) {
        const on = this._form.cellPrecision === value;
        return '<button type="button" class="dating-option' + (on ? ' is-active' : '') + '"'
            + ' data-dating-precision="' + value + '">'
            + '<span class="dating-option-mark dating-option-mark--radio"></span>'
            + '<span class="dating-option-text">'
            + '<span class="dating-option-title">' + escapeHtml(title) + '</span>'
            + '<span class="dating-option-hint">' + escapeHtml(hint) + '</span>'
            + '</span></button>';
    }

    // Kaestchen, nicht Radio: Die drei Absichten schliessen sich nicht aus — man
    // kann fuer „quatschen" und „Beziehung" gleichzeitig offen sein.
    _intentTileHtml(value, title, hint) {
        const on = this._form.intents.has(value);
        return '<button type="button" class="dating-option' + (on ? ' is-active' : '') + '"'
            + ' data-dating-intent="' + escapeHtml(value) + '">'
            + '<span class="dating-option-mark dating-option-mark--check"></span>'
            + '<span class="dating-option-text">'
            + '<span class="dating-option-title">' + escapeHtml(title) + '</span>'
            + '<span class="dating-option-hint">' + escapeHtml(hint) + '</span>'
            + '</span></button>';
    }

    _countryHtml() {
        const opts = ['<option value=""></option>'];
        for (const code of Object.keys(VCS.DatingCore.COUNTRIES)) {
            opts.push('<option value="' + escapeHtml(code) + '"'
                + (this._form.country === code ? ' selected' : '') + '>'
                + escapeHtml(VCS.DatingCore.COUNTRIES[code]) + '</option>');
        }
        return '<select class="app-select dating-select" data-dating-field="country">' + opts.join('') + '</select>';
    }

    // Sprachen aus AVAILABLE_LANGS (translations.js) — dieselbe Menge, die die
    // Uebersetzungs-Pipeline beherrscht, und die ist im Fern-Modus der Punkt.
    _langsHtml() {
        // 'xx' = "Andere Sprache" ganz unten, fuer alle, deren Sprache nicht gelistet ist.
        // Nur hier im Profil angehaengt, NICHT in AVAILABLE_LANGS (die steuert die
        // Uebersetzungs-Pipeline) -- hier ist es reine Profilangabe.
        const codes = AVAILABLE_LANGS.concat('xx');
        const chips = codes.map((code) =>
            '<button type="button" class="dating-chip' + (this._form.langs.has(code) ? ' is-active' : '') + '"'
                + ' data-dating-lang="' + escapeHtml(code) + '">'
                + escapeHtml(LANG_NAMES[code] || code.toUpperCase()) + '</button>'
        ).join('');
        return '<div class="dating-chips">' + chips + '</div>';
    }

    // ── Zusatzfelder: Geburtsdatum / Sternzeichen / Interessen (2026-07-18) ──

    // Drei Dropdowns Tag/Monat/Jahr statt eines nativen Datepickers: der springt bei
    // Geburtstagen am heutigen Datum los und laesst sich schlecht Jahrzehnte zurueckdrehen.
    // Darunter das live berechnete Sternzeichen.
    _birthDateHtml() {
        const f    = this._form;
        const now  = new Date();
        const yMax = now.getFullYear() - VCS.DatingCore.AGE_MIN;   // 18+
        const yMin = now.getFullYear() - 100;
        return '<div class="dating-date-row">'
            + '<select class="app-select dating-select" data-dating-date="day">'
            + this._numOptions(1, 31, f.birthDay, t('datingDay')) + '</select>'
            + '<select class="app-select dating-select" data-dating-date="month">'
            + this._monthOptions(f.birthMonth) + '</select>'
            + '<select class="app-select dating-select" data-dating-date="year">'
            + this._numOptions(yMax, yMin, f.birthYear, t('datingYear')) + '</select>'
            + '</div>'
            + '<div class="dating-starsign" id="datingStarSign">' + this._starSignHtml() + '</div>';
    }

    // from..to inklusive; ist from > to, laeuft es absteigend (fuer die Jahre).
    _numOptions(from, to, selected, placeholder) {
        const opts = ['<option value="">' + escapeHtml(placeholder) + '</option>'];
        const step = from <= to ? 1 : -1;
        for (let n = from; step > 0 ? n <= to : n >= to; n += step) {
            const sel = String(selected) === String(n) ? ' selected' : '';
            opts.push('<option value="' + n + '"' + sel + '>' + n + '</option>');
        }
        return opts.join('');
    }

    // Monatsnamen aus Intl (lokalisiert), damit sie nicht durch die 16 Sprachdateien muessen
    // -- der Browser kennt sie in jeder Sprache.
    _monthOptions(selected) {
        const lang = VCS.state?.currentLang || 'de';
        const opts = ['<option value="">' + escapeHtml(t('datingMonth')) + '</option>'];
        for (let m = 1; m <= 12; m++) {
            let name;
            try {
                name = new Intl.DateTimeFormat(lang, { month: 'long' }).format(new Date(2000, m - 1, 1));
            } catch (_) {
                name = String(m);
            }
            const sel = String(selected) === String(m) ? ' selected' : '';
            opts.push('<option value="' + m + '"' + sel + '>' + escapeHtml(name) + '</option>');
        }
        return opts.join('');
    }

    _starSignHtml() {
        const z = VCS.DatingCore.zodiac(this._form.birthMonth, this._form.birthDay);
        if (!z) {
            return '';
        }
        return '<span class="dating-starsign-badge">' + z.emoji + ' '
            + escapeHtml(t('datingZodiac_' + z.key)) + '</span>';
    }

    // Rauchen/Alkohol: optionale Einfachauswahl. Klick auf den aktiven Chip waehlt ab
    // (zurueck auf "keine Angabe") -- deshalb ein eigener Handler statt data-dating-choice.
    _habitChipsHtml(field, selected) {
        const chips = VCS.DatingCore.HABITS.map((v) =>
            '<button type="button" class="dating-chip' + (v === selected ? ' is-active' : '') + '"'
                + ' data-dating-habit="' + field + '" data-value="' + escapeHtml(v) + '">'
                + escapeHtml(t('datingHabit_' + v)) + '</button>'
        ).join('');
        return '<div class="dating-chips">' + chips + '</div>';
    }

    // Interessen: Mehrfachauswahl wie die Sprachen. Emoji ist sprachuebergreifend, das Label
    // kommt aus den Sprachdateien.
    _interestsHtml() {
        const chips = VCS.DatingCore.INTERESTS.map((it) =>
            '<button type="button" class="dating-chip' + (this._form.interests.has(it.key) ? ' is-active' : '') + '"'
                + ' data-dating-interest="' + escapeHtml(it.key) + '">'
                + it.emoji + ' ' + escapeHtml(t('datingInterest_' + it.key)) + '</button>'
        ).join('');
        return '<div class="dating-chips">' + chips + '</div>';
    }

    _activeSectionHtml() {
        const on = this._profile && this._profile.active;
        return '<div class="dating-active-row">'
            + '<span class="dating-option-text">'
            + '<span class="dating-option-title">' + escapeHtml(t('datingActive')) + '</span>'
            + '<span class="dating-option-hint">' + escapeHtml(t('datingActiveHint')) + '</span>'
            + '</span>'
            + '<button type="button" role="switch" aria-checked="' + (on ? 'true' : 'false') + '"'
            + ' class="dating-switch' + (on ? ' is-on' : '') + '" data-dating-active>'
            + '<span class="dating-switch-knob"></span></button>'
            + '</div>'
            + '<button type="button" class="dating-delete" data-dating-delete>'
            + escapeHtml(t('datingDelete')) + '</button>';
    }

    // Leere url = Bild konnte nicht geladen werden. Dann KEIN <img src="">
    // rendern: Ein leeres src laesst den Browser die Seite selbst als Bild
    // anfordern. Stattdessen ein Platzhalter, wie der errorBuilder in der App.
    _photoImgHtml(url) {
        if (!url) {
            return '<span class="dating-photo-broken">'
                + '<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="1.8" fill="none">'
                + '<rect x="3" y="3" width="18" height="18" rx="2"></rect>'
                + '<path d="M3 15l5-5 4 4 3-3 6 6"></path></svg></span>';
        }
        // draggable="false" ist hier KEIN Detail: Ein <img> ist im Browser von Haus aus
        // ziehbar. Am Desktop startet die Maus deshalb beim Schieben das native
        // Bild-Drag&Drop, der Browser kassiert die Geste, es kommt `pointercancel` — und
        // das Sortieren unten raeumt sich selbst ab. Auf Touch passiert das nicht, deshalb
        // sah der Code beim Bauen richtig aus. (17.07., vom Gruender gemeldet: „Ich kann
        // die Bilder nicht verschieben.")
        return '<img src="' + escapeHtml(url) + '" alt="" draggable="false">';
    }

    _photoRowHtml() {
        const tiles = [];
        // Nur die hochgeladenen lassen sich umsortieren — was noch keinen Hash hat, hat
        // serverseitig auch keine Reihenfolge.
        //
        // Pfeile statt Ziehen (17.07., nach dem Gerätetest): Die selbstgebaute
        // Drag&Drop-Geste (350 ms halten, Pointer-Capture, Neu-Rendern mitten drin) war auf
        // jeder Plattform anders kaputt — Desktop gar nicht, iPhone und Android unzuverlaessig,
        // und was ging, sprang zurueck. Vier Browser, vier Verhalten. Ein Tipp auf einen Pfeil
        // tut ueberall dasselbe und ist ohne Anleitung zu sehen; bei drei Fotos ist Ziehen
        // ohnehin Aufwand ohne Gewinn. Flutter macht es genauso — beide Clients sollen sich
        // gleich anfuehlen.
        const last = this._photos.length - 1;
        this._photos.forEach((p, i) => {
            tiles.push('<div class="dating-photo" data-dating-photo-idx="' + i + '">'
                + this._photoImgHtml(p.url)
                + '<button type="button" class="dating-photo-del" data-dating-photo-del="'
                + escapeHtml(p.hash) + '" aria-label="' + escapeHtml(t('datingPhotoDelete')) + '">&times;</button>'
                // Am Rand kein Pfeil: Was nicht geht, soll man nicht anbieten — dann sieht man
                // am Bild selbst, dass es das erste bzw. letzte ist.
                + (i > 0
                    ? '<button type="button" class="dating-photo-move dating-photo-move--left"'
                        + ' data-dating-photo-move="' + i + '" data-dating-photo-dir="-1"'
                        + ' aria-label="' + escapeHtml(t('datingPhotoMoveLeft')) + '">&lsaquo;</button>'
                    : '')
                + (i < last
                    ? '<button type="button" class="dating-photo-move dating-photo-move--right"'
                        + ' data-dating-photo-move="' + i + '" data-dating-photo-dir="1"'
                        + ' aria-label="' + escapeHtml(t('datingPhotoMoveRight')) + '">&rsaquo;</button>'
                    : '')
                + '</div>');
        });
        // Vorgemerkt: Vorschau direkt aus der Datei. Loeschen ist hier nur ein
        // Vergessen — auf dem Server liegt ja noch nichts.
        this._pending.forEach((p, i) => {
            tiles.push('<div class="dating-photo">'
                + this._photoImgHtml(p.url)
                + '<button type="button" class="dating-photo-del" data-dating-pending-del="' + i + '"'
                + ' aria-label="' + escapeHtml(t('datingPhotoDelete')) + '">&times;</button>'
                + '</div>');
        });
        if (this._photoCount() < VCS.DatingCore.MAX_PHOTOS) {
            tiles.push('<button type="button" class="dating-photo dating-photo-add" data-dating-photo-add'
                + ' aria-label="' + escapeHtml(t('datingPhotoAdd')) + '">'
                + '<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="1.8" fill="none">'
                + '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>'
                + '<circle cx="12" cy="13" r="4"></circle></svg></button>');
        }
        return tiles.join('');
    }

    // Kein Nachbinden noetig: Die Pfeile haengen am delegierten Klick-Handler in
    // _bindEvents(), nicht an den Kacheln. Genau daran krankte die alte Drag-Geste — ihre
    // Handler klebten am Element, und jedes Neu-Rendern kappte sie.
    _refreshPhotoRow() {
        $('#datingPhotoRow').html(this._photoRowHtml());
    }

    // ── Fotos sortieren (Pfeile) ─────────────────────────────
    //
    // Das erste Foto entscheidet ueber ein Ja oder Nein; bis 17.07. war das zwangslaeufig
    // das zuerst hochgeladene. Die Spalte sortOrder gab es seit dem ersten Tag, aber keinen
    // Weg, sie zu aendern.
    //
    // Hier stand bis 17.07. abends eine selbstgebaute Drag&Drop-Geste (Pointer-Events,
    // 350 ms halten, Pointer-Capture). Sie ist RAUS, und das ist die Lehre des Tages:
    //
    //   Desktop  — unbedienbar: Der 8-px-Guard gegen versehentliches Scrollen warf den
    //              Halte-Timer weg, bevor er ablief. Mit der Maus zieht man sofort los.
    //   iPhone   — unzuverlaessig.
    //   Android  — unzuverlaessig.
    //   und was ging, sprang zurueck.
    //
    // Vier Plattformen, vier Verhalten, und keins davon ohne Geraet zu diagnostizieren.
    // Ein Tipp auf einen Pfeil tut ueberall dasselbe, ist ohne Anleitung zu sehen und hat
    // weder Haltezeit noch Capture, der brechen kann. Bei DREI Fotos ist Ziehen ohnehin
    // Aufwand ohne Gewinn. **Flutter macht es genauso** (Wunsch des Gruenders: beide
    // Clients sollen sich gleich anfuehlen) — dort flog ReorderableListView dafuer raus.
    //
    // Was bleibt: derselbe POST /dating/photos/order, dieselbe optimistische Anzeige,
    // dieselbe Ruecknahme bei Serverfehler.
    async _movePhoto(index, dir) {
        if (this._busy) {
            return;
        }
        const target = index + dir;
        if (index < 0 || index >= this._photos.length || target < 0 || target >= this._photos.length) {
            return;
        }

        // Optimistisch: erst zeigen, dann speichern. Ein Foto, das sich nach einem Tipp
        // erst eine Netzrunde spaeter bewegt, fuehlt sich kaputt an.
        const before = this._photos;
        const next   = this._photos.slice();
        [next[index], next[target]] = [next[target], next[index]];
        this._photos = next;
        this._refreshPhotoRow();

        const saved = await VCS.datingCore.setPhotoOrder(next.map((p) => p.hash));
        if (!saved) {
            // Nicht gespeichert — zurueck auf den Stand, der wirklich beim Server liegt.
            // Sonst zeigt das Profil eine Reihenfolge, die es nur hier gibt.
            this._photos = before;
            this._refreshPhotoRow();
            VCS.ui?.showToast?.(t('datingErrUnknown'), 'error');
            return;
        }
        // Die Serverliste ist massgeblich — er nummeriert alle Fotos neu, auch die, die wir
        // nicht geschickt haben. Die Blob-URLs behalten wir dabei: Sie haengen am Hash,
        // nicht an der Position, und ein Neuladen waere reine Warterei.
        const byHash = new Map(this._photos.map((p) => [p.hash, p]));
        this._photos = saved.map((h) => byHash.get(h)).filter(Boolean);
        this._refreshPhotoRow();
    }

    _setBusy(on) {
        this._busy = on;
        $('#datingBusy').toggleClass('hidden', !on);
    }

    // ── Events ───────────────────────────────────────────────

    _bindEvents() {
        const body = this._view.body;

        // Foto eine Position nach links oder rechts. Delegiert an den Body — die Kacheln
        // entstehen bei jedem Rendern neu, ein Handler an der Kachel selbst waere nach dem
        // ersten Verschieben tot. (Genau das war der Kern des Drag-Debakels vom 17.07.)
        body.on('click', '[data-dating-photo-move]', (e) => {
            const el  = $(e.currentTarget);
            const idx = parseInt(el.data('dating-photo-move'), 10);
            const dir = parseInt(el.data('dating-photo-dir'), 10);
            if (!Number.isFinite(idx) || !Number.isFinite(dir)) {
                return;
            }
            this._movePhoto(idx, dir);
        });

        // Texteingaben schreiben sofort in den Zustand — ein Moduswechsel zeichnet
        // Abschnitte neu, und was nur im DOM stand, waere dann weg.
        body.on('input', '[data-dating-field]', (e) => {
            const field = $(e.currentTarget).data('dating-field');
            this._form[field] = e.currentTarget.value;
        });
        body.on('change', 'select[data-dating-field]', (e) => {
            const field = $(e.currentTarget).data('dating-field');
            this._form[field] = e.currentTarget.value;
        });

        // Die beiden Griffe duerfen sich nicht ueberkreuzen: Der untere schiebt
        // nicht ueber den oberen und umgekehrt.
        body.on('input', '[data-dating-age]', (e) => {
            const which = String($(e.currentTarget).data('dating-age'));
            let v = parseInt(e.currentTarget.value, 10);
            if (!Number.isFinite(v)) {
                return;
            }
            if (which === 'min') {
                v = Math.min(v, this._form.seekAgeMax);
                this._form.seekAgeMin = v;
            } else {
                v = Math.max(v, this._form.seekAgeMin);
                this._form.seekAgeMax = v;
            }
            e.currentTarget.value = v;
            this._updateSeekAge();
        });

        body.on('input', '[data-dating-radius]', (e) => {
            const v = parseInt(e.currentTarget.value, 10);
            if (!Number.isFinite(v)) {
                return;
            }
            this._form.seekRadiusKm = v;
            this._updateSeekRadius();
        });

        body.on('click', '[data-dating-choice]', (e) => {
            const field = $(e.currentTarget).data('dating-choice');
            const value = String($(e.currentTarget).data('value'));
            this._form[field] = value;
            $(e.currentTarget).siblings().removeClass('is-active');
            $(e.currentTarget).addClass('is-active');
        });

        body.on('click', '[data-dating-mode]', (e) => {
            const value = String($(e.currentTarget).data('dating-mode'));
            if (this._form.mode === value) {
                return;
            }
            this._form.mode = value;
            $(e.currentTarget).siblings('[data-dating-mode]').removeClass('is-active');
            $(e.currentTarget).addClass('is-active');
            // Nur die betroffenen Abschnitte umschalten statt alles neu zu zeichnen:
            // ein Neuaufbau wuerde den Fokus aus dem Feld reissen, in dem man steht.
            $('#datingIntentSection').toggleClass('hidden', value === 'remote');
            $('#datingPrecisionSection').toggleClass('hidden', value === 'remote');
            $('#datingCountrySection').toggleClass('hidden', value === 'local');
            // Umkreis nur bei „nur in meiner Naehe" — bei „Beides" ist der Ort egal.
            $('#datingRadiusSection').toggleClass('hidden', value !== 'local');
        });

        body.on('click', '[data-dating-precision]', (e) => {
            const value = parseInt($(e.currentTarget).data('dating-precision'), 10);
            if (!VCS.DatingCore.PRECISIONS.includes(value)) {
                return;
            }
            this._form.cellPrecision = value;
            $(e.currentTarget).siblings('[data-dating-precision]').removeClass('is-active');
            $(e.currentTarget).addClass('is-active');
        });

        body.on('click', '[data-dating-intent]', (e) => {
            const value = String($(e.currentTarget).data('dating-intent'));
            if (this._form.intents.has(value)) {
                this._form.intents.delete(value);
                $(e.currentTarget).removeClass('is-active');
            } else {
                this._form.intents.add(value);
                $(e.currentTarget).addClass('is-active');
            }
        });

        body.on('click', '[data-dating-lang]', (e) => {
            const code = String($(e.currentTarget).data('dating-lang'));
            if (this._form.langs.has(code)) {
                this._form.langs.delete(code);
                $(e.currentTarget).removeClass('is-active');
            } else {
                this._form.langs.add(code);
                $(e.currentTarget).addClass('is-active');
            }
        });

        // Geburtsdatum-Dropdowns: Wert in den Zustand schreiben und das Sternzeichen
        // sofort nachziehen. Das Jahr fuellt weiter birthYear (Alter), Monat/Tag das Zeichen.
        body.on('change', '[data-dating-date]', (e) => {
            const which = String($(e.currentTarget).data('dating-date'));
            const value = e.currentTarget.value;
            if (which === 'day')   { this._form.birthDay   = value; }
            if (which === 'month') { this._form.birthMonth = value; }
            if (which === 'year')  { this._form.birthYear  = value; }
            $('#datingStarSign').html(this._starSignHtml());
        });

        // Rauchen/Alkohol: Einfachauswahl mit Abwaehlen. Klick auf den aktiven Chip setzt
        // das Feld zurueck auf '' (keine Angabe).
        body.on('click', '[data-dating-habit]', (e) => {
            const field    = String($(e.currentTarget).data('dating-habit'));
            const value    = String($(e.currentTarget).data('value'));
            const isActive = $(e.currentTarget).hasClass('is-active');
            $(e.currentTarget).siblings('[data-dating-habit]').removeClass('is-active');
            if (isActive) {
                this._form[field] = '';
                $(e.currentTarget).removeClass('is-active');
            } else {
                this._form[field] = value;
                $(e.currentTarget).addClass('is-active');
            }
        });

        body.on('click', '[data-dating-interest]', (e) => {
            const key = String($(e.currentTarget).data('dating-interest'));
            if (this._form.interests.has(key)) {
                this._form.interests.delete(key);
                $(e.currentTarget).removeClass('is-active');
                return;
            }
            if (this._form.interests.size >= VCS.DatingCore.INTERESTS_MAX) {
                VCS.ui?.showToast?.(t('datingInterestsMax'), 'error');
                return;
            }
            this._form.interests.add(key);
            $(e.currentTarget).addClass('is-active');
        });

        body.on('click', '[data-dating-photo-add]',   () => this._addPhoto());
        body.on('click', '[data-dating-photo-del]',   (e) => this._deletePhoto(String($(e.currentTarget).data('dating-photo-del'))));
        body.on('click', '[data-dating-pending-del]', (e) => this._forgetPending(parseInt($(e.currentTarget).data('dating-pending-del'), 10)));
        body.on('click', '[data-dating-save]',        () => this._save());
        body.on('click', '[data-dating-active]',      () => this._toggleActive());
        body.on('click', '[data-dating-delete]',      () => this._confirmDelete());
    }

    // ── Fotos ────────────────────────────────────────────────

    _addPhoto() {
        if (this._busy) {
            return;
        }
        if (this._photoCount() >= VCS.DatingCore.MAX_PHOTOS) {
            VCS.ui?.showToast?.(t('datingErrTooManyPhotos'), 'error');
            return;
        }
        const input = document.createElement('input');
        input.type   = 'file';
        input.accept = 'image/*';
        input.addEventListener('change', async () => {
            const file = input.files && input.files[0];
            if (!file) {
                return;
            }
            const blob = await this._compressPhoto(file);

            // Noch kein Handle: vormerken. Fotos haengen serverseitig am Handle,
            // und den gibt es erst nach dem ersten Speichern. Statt den Nutzer
            // deshalb ganz oben im Formular auszubremsen, laden wir das Bild
            // beim Speichern hoch — so laesst sich das Formular von oben nach
            // unten ausfuellen. (In der App teuer gelernt.)
            if (!this._profile) {
                this._pending.push({ file: blob, url: URL.createObjectURL(blob) });
                this._refreshPhotoRow();
                console.log('[dating] Foto vorgemerkt (noch kein Profil): ' + this._pending.length);
                return;
            }

            this._setBusy(true);
            const up = await VCS.datingCore.uploadPhoto(blob);
            this._setBusy(false);
            if (!up.hash) {
                // Sagt jetzt, WARUM — bei einer Ablehnung ist das der ganze Unterschied
                // zwischen „nochmal versuchen" und „anderes Bild nehmen".
                VCS.ui?.showToast?.(VCS.DatingCore.photoErrorText(up.error), 'error');
                return;
            }
            const url = await VCS.datingCore.loadPhotoUrl(up.hash);
            this._photos.push({ hash: up.hash, url: url });
            this._refreshPhotoRow();
        });
        input.click();
    }

    async _deletePhoto(hash) {
        if (this._busy) {
            return;
        }
        this._setBusy(true);
        const ok = await VCS.datingCore.deletePhoto(hash);
        this._setBusy(false);
        if (!ok) {
            VCS.ui?.showToast?.(t('datingErrUnknown'), 'error');
            return;
        }
        const gone = this._photos.filter((p) => p.hash === hash);
        gone.forEach((p) => VCS.datingCore.releasePhotoUrl(p.url));
        this._photos = this._photos.filter((p) => p.hash !== hash);
        this._refreshPhotoRow();
    }

    _forgetPending(index) {
        const p = this._pending[index];
        if (!p) {
            return;
        }
        VCS.datingCore.releasePhotoUrl(p.url);
        this._pending.splice(index, 1);
        this._refreshPhotoRow();
    }

    // Vorgemerkte Fotos hochladen. Erst nach dem Speichern aufrufen — vorher gibt
    // es keinen Handle, an dem sie haengen koennten. Was scheitert, bleibt
    // vorgemerkt und laesst sich mit dem naechsten Speichern erneut versuchen.
    // Gibt den Fehlercode des ERSTEN Fehlschlags zurueck ('' = alles gut). Nicht bloss
    // true/false: Ein abgelehntes Foto braucht seinen eigenen Text, sonst liest der Nutzer
    // „konnte nicht hochgeladen werden" und probiert dasselbe Bild noch dreimal.
    //
    // Der erste Grund reicht: Fast immer ist nur ein Foto vorgemerkt, und bei mehreren ist
    // ein konkreter Grund besser als eine Liste, die niemand liest.
    async _uploadPending() {
        if (!this._pending.length) {
            return '';
        }
        const failed = [];
        let firstError = '';
        for (const p of this._pending) {
            const up = await VCS.datingCore.uploadPhoto(p.file);
            if (!up.hash) {
                failed.push(p);
                if (!firstError) {
                    firstError = up.error || 'unknown';
                }
                continue;
            }
            VCS.datingCore.releasePhotoUrl(p.url);
            const url = await VCS.datingCore.loadPhotoUrl(up.hash);
            this._photos.push({ hash: up.hash, url: url });
        }
        this._pending = failed;
        console.log('[dating] Vorgemerkte Fotos hochgeladen, offen=' + failed.length
            + (firstError ? ' grund=' + firstError : ''));
        return firstError;
    }

    // Seitenverhaeltnis-treu auf PHOTO_MAX_PX verkleinern, als JPEG. Muster:
    // chat-setup.js _compressAvatar (dort 512 px fuer Avatare).
    _compressPhoto(file, maxSize = VCS.DatingProfile.PHOTO_MAX_PX, quality = VCS.DatingProfile.PHOTO_QUALITY) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let w = img.width, h = img.height;
                if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
                else       { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
                canvas.width  = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', quality);
                URL.revokeObjectURL(img.src);
            };
            img.onerror = () => { URL.revokeObjectURL(img.src); resolve(file); };
            img.src = URL.createObjectURL(file);
        });
    }

    // ── Speichern ────────────────────────────────────────────

    // Server-Fehlercodes in die Landessprache. Der Server validiert selbst nochmal
    // — die Pruefungen hier sind nur dafuer da, dass man nicht auf eine Antwort
    // warten muss.
    _errorText(code) {
        switch (code) {
            case 'invalid_name'     : return t('datingErrName');
            case 'invalid_bio'      : return t('datingErrBio');
            case 'invalid_age'      : return t('datingErrAge');
            case 'invalid_age_range': return t('datingErrAgeRange');
            case 'invalid_radius'   : return t('datingErrRadius');
            case 'invalid_country': return t('datingErrCountry');
            case 'invalid_intent' : return t('datingErrIntent');
            case 'invalid_langs'  : return t('datingErrLangs');
            case 'network'        : return t('datingErrNetwork');
            default               : return t('datingErrUnknown');
        }
    }

    async _save() {
        if (this._busy) {
            return;
        }
        const f    = this._form;
        // Name IMMER aus dem allgemeinen Profil, nie aus dem (schreibgeschuetzten)
        // Formularfeld. Wird nur gelesen -- der Chat-Name selbst bleibt unberuehrt.
        const name   = this._profileName();
        const bio    = (f.bio || '').trim();
        const year   = parseInt(f.birthYear, 10) || 0;
        const month  = parseInt(f.birthMonth, 10) || 0;
        const day    = parseInt(f.birthDay, 10) || 0;
        const height = parseInt(f.heightCm, 10) || 0;

        if (!name)                  { return VCS.ui?.showToast?.(t('datingErrName'), 'error'); }
        if (!bio)                   { return VCS.ui?.showToast?.(t('datingErrBio'), 'error'); }
        // Volles Datum ist jetzt Pflicht (fuers Sternzeichen). Das Alter kommt aus dem Jahr.
        if (!year || !month || !day) { return VCS.ui?.showToast?.(t('datingErrBirthDate'), 'error'); }
        if (new Date().getUTCFullYear() - year < 18) {
            return VCS.ui?.showToast?.(t('datingErrAge'), 'error');
        }
        // Groesse ist optional; nur pruefen, wenn angegeben.
        if (height && (height < VCS.DatingCore.HEIGHT_MIN || height > VCS.DatingCore.HEIGHT_MAX)) {
            return VCS.ui?.showToast?.(t('datingErrHeight'), 'error');
        }
        // Untergrenze 18 auch hier: Niemand soll nach Minderjaehrigen suchen
        // koennen, auch nicht ins Leere. Der Server prueft dasselbe.
        if (f.seekAgeMin < VCS.DatingCore.AGE_MIN || f.seekAgeMax > VCS.DatingCore.AGE_MAX
                || f.seekAgeMin > f.seekAgeMax) {
            return VCS.ui?.showToast?.(t('datingErrAgeRange'), 'error');
        }
        // Umkreis 1..500 km. Der Server prueft dasselbe (invalid_radius).
        if (f.mode === 'local' && (f.seekRadiusKm < VCS.DatingCore.RADIUS_MIN
                || f.seekRadiusKm > VCS.DatingCore.RADIUS_ANY)) {
            return VCS.ui?.showToast?.(t('datingErrRadius'), 'error');
        }
        if (!f.langs.size)          { return VCS.ui?.showToast?.(t('datingErrLangs'), 'error'); }
        if (f.mode !== 'local' && !f.country) {
            return VCS.ui?.showToast?.(t('datingErrCountry'), 'error');
        }
        // Im Fern-Modus wird die Frage gar nicht gestellt, also auch nicht eingefordert.
        if (f.mode !== 'remote' && !f.intents.size) {
            return VCS.ui?.showToast?.(t('datingErrIntent'), 'error');
        }

        this._setBusy(true);
        const result = await VCS.datingCore.saveProfile({
            displayName: name,
            bio        : bio,
            birthYear  : year,
            // Zusatzfelder (2026-07-18). Monat/Tag fuers Sternzeichen, Rest optionale Angaben.
            birthMonth : month,
            birthDay   : day,
            heightCm   : height,
            smoking    : f.smoking || '',
            drinking   : f.drinking || '',
            interests  : [...f.interests],
            gender     : f.gender,
            seeking    : f.seeking,
            seekAgeMin : f.seekAgeMin,
            seekAgeMax : f.seekAgeMax,
            // Nur der Nah-Modus kennt einen Umkreis. „Beides" heisst, dass der Ort
            // egal ist, und „andere Laender" fragt nach dem Land — in beiden Faellen
            // den Anschlag senden, statt eine Zahl stehenzulassen, nach der niemand
            // gefragt hat.
            seekRadiusKm: f.mode === 'local' ? f.seekRadiusKm : VCS.DatingCore.RADIUS_ANY,
            // Im Fern-Modus wird nie geortet — dann ist die Genauigkeit
            // gegenstandslos, und wir schicken das Ungefaehre, statt eine
            // Einstellung stehenzulassen, die niemand sieht.
            cellPrecision: f.mode === 'remote' ? VCS.DatingCore.PRECISION_ROUGH : f.cellPrecision,
            mode       : f.mode,
            langs      : [...f.langs],
            // Absicht gilt nur fuers Treffen vor Ort — im Fern-Modus gar nicht
            // erst mitschicken.
            intents    : f.mode === 'remote' ? [] : [...f.intents],
            country    : f.country
        });

        if (!result.ok) {
            this._setBusy(false);
            VCS.ui?.showToast?.(this._errorText(result.error), 'error');
            return;
        }

        const wasNew  = !this._profile;
        this._profile = result.profile;
        // Jetzt gibt es einen Handle — vorgemerkte Fotos koennen hoch.
        const photoError = await this._uploadPending();
        this._setBusy(false);

        // Beim ersten Speichern kommen Sichtbarkeit und Loeschen dazu, die es
        // ohne Profil nicht gab.
        if (wasNew) {
            this._render();
        } else {
            this._refreshPhotoRow();
        }

        // Gespeichert = neuer sauberer Zustand. Jetzt ist nichts mehr offen (ausser
        // ein Foto blieb vorgemerkt, weil es abgelehnt wurde — das steckt ueber
        // _pending noch in der Signatur und bleibt zu Recht "dirty").
        this._clean = this._formSignature();

        // Das Profil IST gespeichert — nur ein Foto ging nicht mit. Deshalb der Grund des
        // Fotos und nicht „Speichern fehlgeschlagen": Wer „abgelehnt" liest, weiss, dass er
        // ein anderes Bild braucht, und nicht, dass seine Eingaben weg sind.
        if (photoError) {
            VCS.ui?.showToast?.(VCS.DatingCore.photoErrorText(photoError), 'error');
        } else {
            VCS.ui?.showToast?.(t('datingSaved'), 'success');
        }
    }

    // ── Sichtbarkeit / Loeschen ──────────────────────────────

    async _toggleActive() {
        if (this._busy || !this._profile) {
            return;
        }
        const next = !this._profile.active;
        // Sichtbar geht nur mit Foto — sonst stuende man ohne Bild im Kartenstapel.
        if (next && !this._photos.length) {
            VCS.ui?.showToast?.(t('datingErrPhoto'), 'error');
            return;
        }

        this._setBusy(true);
        const ok = await VCS.datingCore.setActive(next);
        this._setBusy(false);
        if (!ok) {
            VCS.ui?.showToast?.(t('datingErrUnknown'), 'error');
            return;
        }
        this._profile.active = next;
        $('[data-dating-active]')
            .toggleClass('is-on', next)
            .attr('aria-checked', next ? 'true' : 'false');
    }

    async _confirmDelete() {
        if (this._busy || !this._profile) {
            return;
        }
        const ok = await VCS.ui?.showConfirm?.(t('datingDeleteBody'), '', t('datingDelete'));
        if (!ok) {
            return;
        }

        this._setBusy(true);
        const done = await VCS.datingCore.deleteProfile();
        this._setBusy(false);
        if (!done) {
            VCS.ui?.showToast?.(t('datingErrUnknown'), 'error');
            return;
        }

        this._releaseUrls();
        this._profile = null;
        this._form    = this._emptyForm();
        this._render();
        // Nach dem Loeschen ist das leere Formular der saubere Zustand.
        this._clean = this._formSignature();
        VCS.ui?.showToast?.(t('datingDeleted'), 'success');
    }
};
